import { BaseUser } from "./common.types";
import { OpportunityDetail } from "./opportunity.types";
import { Team, UserList } from "./user.types";

// Work state enum
export enum WorkStateEnum {
  NotSet = 0,
  Open = 1,
  Closed = 2,
  Draft = 3,
  Solved = 4,
}

// Work type enum - extensive list of work types
export enum WorkTypeEnum {
  NotSet = 0,
  Other = 1,
  Opportunity = 2,
  Text = 3,
  Prospect = 4,
  ExternalSaleContract = 5,
  UndeliverableDocument = 6,
  SoftReminder = 7,
  PowerOfAttorney = 8,
  Z05 = 9,
  ChargeBoxWork = 10,
  Z04 = 11,
  Leads = 12,
  ManualContractCancellation = 13,
  FöretagIKonkurs = 14,
  FöretagILikvidation = 15,
  MarkavtalSwishOut = 16,
  CustomerWorkFromWeb = 17,
  ChangeInEnskildFirma = 18,
  DalaVind2023 = 19,
  DalaHemlarmInterest = 20,
  IncomingEmail = 21,
  AppMessage = 22,
  PowerOutage = 23,
  EnergyPoolOrder = 24,
  DalaVind2024 = 25,
  SharedEnergySystem = 26,
  AvestaElpool2024 = 27,
  Estate = 28,
  DalaVindNyhetsbrev = 29,
  FöretagIRekonstruktion = 30,
  BrfGruven2025 = 31,
  WelcomeCallExternalSale = 32,
  EnergyMemberPayout = 33,
  SecureVolume = 34,
}

// Work item state enum
export enum WorkItemStateEnum {
  NotSet = 0,
  Active = 1,
  Closed = 2,
  Draft = 3,
  Solved = 4,
}

// Work item favorite type
export enum WorkItemFavoriteTypeEnum {
  NotSet = 0,
  Favorite = 1,
}

// Work item type enum - extensive list of work item types
export enum WorkItemTypeEnum {
  NotSet = 0,
  OpportunityCall = 1,
  HandleEmail = 2,
  PhysicalMeeting = 3,
  PhoneCall = 4,
  Email = 5,
  Sms = 6,
  WelcomeCall = 7,
  ReconnectToCustomer = 8,
  FullmaktRejected = 9,
  ContractProductionFacility = 10,
  Z04 = 11,
  CreateContractFailed = 12,
  AvtalsBrott = 13,
  MessageExternalSale = 14,
  ManualContractCancellation = 15,
  NegativAperak = 16,
  KanEjSkickaZ03 = 17,
  EmailError = 18,
  MissingPoaFile = 19,
  Z05LK = 20,
  DigitalMeeting = 21,
  Admin = 22,
  ContactAttempt = 23,
  FollowUpEmail = 1001,
  FollowUpPhoneCall = 1002,
  FollowUpSms = 1003,
  FollowUpDigitalMeeting = 1004,
  FollowUpPhysicalMeeting = 1005,
}

// Category type
export interface CategoryList {
  Id: number;
  Name: string;
}

// Customer reference in work context
export interface CustomerList {
  Id: number;
  Name: string;
  GivenName: string;
  CustomerId: string;
  CompanyNo: number;
}

// Main work interface
export interface WorkList {
  Id: number;
  Modified: string;
  Created: string;
  Name: string;
  Description: string;
  Type: WorkTypeEnum;
  State: WorkStateEnum;
  Customer: CustomerList | null;
  ParentId: number | null;
  Team: TeamList;
  Category: CategoryList;
  CategoryId: number | null;
  AssignedTo: BaseUser | null;
  ChangedBy: BaseUser;
  CreatedBy: BaseUser;
}

// Team list with users
export interface TeamList extends Team {
  TeamSelectable: boolean;
  Users: BaseUser[];
}

export interface WorkItemList {
  Id: number;
  Name: string;
  CanSolveWork: boolean;
  FavoriteType: WorkItemFavoriteTypeEnum;
  ExternalId?: string;
  Modified: string;
  CompleteBefore?: string | null;
  Category: { Id: number; Name: string };
  ScheduledTo: string;
  Type: WorkItemTypeEnum;
  Created: string;
  Result: string | undefined | null;
  State: WorkItemStateEnum;
  Team: {
    Id: number;
    Name: string;
  };
  ChangedBy: UserList;
  CreatedBy: UserList;
  AssignedTo: UserList | null;
  Customer: {
    Id: number;
    Name: string;
    GivenName: string;
    CustomerId: number;
  };
  Description: string;
  WorkId: number | null;
}

// Work details
export interface WorkDetails {
  Id: number;
  Type: number;
  Name: string;
  Time: string;
  Modified: string;
  Created: string;
  Data: string;
  IsActive: boolean;
  ChangedBy: BaseUser;
  CreatedBy: BaseUser;
  ParentEventDataId: number | null;
  StateId: number;
  CategoryId: number | null;
  CategoryName: string | null;
}

// User work items parameters
export interface UserWorkItemsParams {
  fromDate?: string;
  toDate?: string;
  state?: WorkItemStateEnum;
  userId?: number;
}

// User works parameters
export interface UserWorksParams {
  fromDate?: string;
  toDate?: string;
  state?: WorkStateEnum;
  userId?: number;
}

// Next action for work items
export interface NextAction {
  NextActionId: number;
  CustomerNo: number;
  NextActionDate: string;
  NextActionType: string;
  NextActionResponsible: string;
  TeamId: number | null;
  Status: boolean;
  Created: string;
  Modified: string | null;
  CreatedBy: number;
  ChangedBy: number | null;
}

export interface Work {
  Id: number;
  Modified: string;
  Created: string;
  ParentId: number | null;
  Name: string;
  Description: string;
  Type: number;
  State: number;
  Category: {
    Id: number;
    Name: string;
  };
  Customer: {
    Id: number;
    Name: string;
    GivenName: string;
    CustomerId: string;
    CompanyNo: number;
  };
  ChangedBy: UserList;
  CreatedBy: UserList;
  Team: {
    Id: number;
    Name: string;
    TeamSelectable: boolean;
    Users: BaseUser[] | null;
  };
  AssignedTo: UserList | null;
  ClosedBy: UserList | null;
  WorkItems: WorkItemList[];
  EventDatas: EventData[];
  EventLogs: EventLog[];
  HasUnansweredEmail: boolean;
  Opportunity?: CrmOpportunity;
}

export interface EventData {
  Id: number;
  Type: EventDataTypeEnum;
  Name: string;
  Time: string; // ISO 8601 string, e.g. "2025-06-18T12:34:56Z"
  Modified: string;
  Created: string;
  Data: string;
  ChangedBy: UserList;
  CreatedBy: UserList;
  Status: boolean;
  ParentEventDataId?: number;
  // WorkNo is ignored in JSON (JsonIgnore), so it may not be needed unless handled manually
}

export enum EventDataTypeEnum {
  FileConfirmationSent = 1,
  FileInvoiceSent = 2,
  FileCombinedClaim = 3,
  Opportunity = 4,
  Text = 5,
  Lead = 6,
  Call = 7,
  IncomingEmail = 8,
  OutgoingEmail = 9,
  OutgoingSms = 10,
  FileCrmDocument = 11,
  OutgoingCall = 12,
  ContractProductConfiguratorData = 13,
  RenewContractProductConfiguratorData = 14,
  CustomerAddressUpdate = 15,
  Discount = 16,
  ExternalSaleRequestRaw = 17,
  ExternalSaleFiles = 18,
  ExternalSaleSignAgreement = 19,
  ContractProductConfiguratorCreateDraft = 20,
  ContractFile = 21,
  ContractProductConfigurationRenewDraft = 22,
  CustomerNote = 23,
  ExternalUserLogin = 24,
  SoftReminderClaim = 25,
  BankIdSignedData = 26,
  ContractFiles = 27,
  DirectDebitRequest = 28,
  PoaInformation = 29,
  CustomerDocument = 30,
  CustomerPoaDocument = 31,
  BankIDSignedFile = 32,
  SwishCustomerPayout = 33,
  IncomingPoaInformation = 34,
  ChargeBoxInformation = 35,
  RenewContractFixedVolumeData = 36,
  Z04Content = 37,
  SuggestedCustomerContent = 38,
  LeadsData = 39,
  OpenEInvoiceBank = 40,
  WinbackContract = 41,
  UseDirectDebit = 42,
  ContractCancellation = 43,
  MoveOutReminderCommunication = 44,
  MarkavtalSwishOut = 45,
  ExternalSalePayout = 46,
  DalaVind2023 = 47,
  AppPush = 48,
  UnprocessedEmail = 49,
  UnprocessedSpamEmail = 50,
  Apsis = 51,
  IncomingAppMessage = 52,
  OutgoingAppMessage = 53,
  SurveyResponse = 54,
  // EnergyPoolSelling = 55,        // Commented out as in C# version
  // EnergyPoolBuying = 56,         // Commented out as in C# version
  // EnergyPoolTransactionOrder = 57,// Commented out as in C# version
  DalaVind2024 = 58,
  TrustPilotReview = 59,
  EnergyMember = 60,
  EnergyMemberTransaction = 61,
  AvestaElpool2024 = 62,
  SendMail = 63,
  SendMailPdf = 64,
  DalaVind2025 = 65,
  ForwardedEmail = 66,
  NegativeAperak = 67,
  BrfGruven2025 = 68,
  AddedJob = 69,
  ErrorJob = 70,
  BrazeEvent = 71,
  EnergyMemberPayout = 72,
  SecureVolume = 73,
  SecureVolumeContractFile = 74,
  SecureVolumeSignedContractFile = 75,
}

export interface EventLog {
  Id: number;
  Description: string;
  Type: EventLogTypeEnum;
  Time: string;
  CreatedBy: UserList;
  Created: string; // ISO-format
}

export enum EventLogTypeEnum {
  Unset = 0,
  AssignWorkItem = 1,
  AssignWork = 2,
  UpdateObject = 3,
  ReopenWork = 4,
  ChangeCategory = 5,
  WorkChangeState = 6,
  UpdateCustomerSalesPerson = 7,
  UpdateCustomerGroup = 8,
  AssignTeam = 9,
  WelcomeCall = 10,
  UpdateInvoiceOptionsFromPortalService = 11,
  MinElUpdateInvoiceEmail = 12,
  UpdateInvoiceDelivery = 13,
  UpdateCommunicationFromWeb = 14,
  ChangeTeam = 15,
  RenewContractFromWeb = 16,
  UpdateManualMonthlyBillingPoint = 17,
  UpdateInvoiceAddressFromWeb = 18,
  WinbackCustomerContract = 19,
  UseDirectDebit = 20,
}
export interface CrmOpportunity {
  OpportunityNo: number;
  CompanyNo: number;
  WorkNo: number;
  Comment?: string;
  Created: string;
  CreatedBy: number;
  Changed?: string;
  ChangedBy?: number;
  Status: boolean;
  Details: OpportunityDetail[];
}
